import { useState } from "react";
import type { BundleResolution } from "@nebulize/sdk";

import {
  Button,
  DataTable,
  EmptyState,
  Page,
  Panel,
  Stack,
  StatusBadge,
} from "@/components/ui";

import { AvailabilityBadge, CanvasKindChip, CanvasScopeChip, Chip } from "../components/Chips";
import { GatesEditor } from "../components/GatesEditor";
import { Requirements, RequirementsPreview } from "../components/Requirements";
import { RunBadge } from "../components/RunBadge";
import { SettingControl } from "../components/SettingControl";
import { Eyebrow, Mono, Muted } from "../components/Text";
import { canvasesOf, lifecycleOf, rolesOf, settingsOf } from "../lib/catalog";
import { isLive, runStatusOf, verbsFor } from "../lib/runState";
import { useActivityVerb, useRemoveActivity, useSetting, useSpaceActivities } from "../spaces/lib/queries";
import type { AccessAddress, AddedActivityRow, CatalogEntry, ProposedBinding, SpaceRow, VersionAccess } from "../types";

import {
  useBundleResolution,
  useCatalogEntry,
} from "./lib/queries";

/** The sdk exports the resolution and not its member shape. */
type ResolvedMember = BundleResolution["members"][number];

function Settings({
  entry,
  added,
}: {
  readonly entry: CatalogEntry;
  readonly added: AddedActivityRow | null;
}) {
  const fields = settingsOf(entry);
  const setting = useSetting(added?.added_activity_id ?? "");

  if (fields.length === 0) return null;

  if (added === null) {
    return (
      <Panel title="Settings this activity declares" padding="none">
        <DataTable
          caption={`Settings ${entry.name} declares`}
          columns={[
            { key: "key", header: "Setting", emphasis: true, mono: true },
            { key: "label", header: "Label" },
            { key: "type", header: "Type", mono: true },
            { key: "default", header: "Default", numeric: true },
          ]}
          rows={fields}
          emptyHeadline="No settings"
        />
      </Panel>
    );
  }

  return (
    <Panel title="Settings">
      <div className="cx-config">
        {fields.map((field) => (
          <SettingControl
            key={field.key}
            field={field}
            value={added.settings[field.key] ?? field.default}
            onChange={(next) => {
              setting.mutate({ key: field.key, value: next });
            }}
          />
        ))}
      </div>
    </Panel>
  );
}

/** One activity of a bundle, or the whole of a plain one: what it draws, needs and holds. */
function NodeConfig({
  entry,
  added,
  spaceId,
}: {
  readonly entry: CatalogEntry;
  readonly added: AddedActivityRow | null;
  readonly spaceId: string;
}) {
  const verb = useActivityVerb();
  const status = added === null ? null : runStatusOf(added.mount);
  const canvases = canvasesOf(entry);
  const roles = rolesOf(entry);

  return (
    <>
      <Panel
        title={entry.name}
        subtitle={entry.description === "" ? undefined : entry.description}
        action={
          <Stack direction="row" gap={3} align="center">
            {added === null ? (
              <StatusBadge tone="neutral">not added</StatusBadge>
            ) : (
              <RunBadge mount={added.mount} />
            )}
            {added === null || status === null
              ? null
              : verbsFor(status).map((one) => (
                  <Button
                    key={one}
                    size="sm"
                    variant={one === "Start" || one === "Resume" ? "primary" : "ghost"}
                    loading={verb.isPending}
                    onClick={() => {
                      verb.mutate({ row: added, verb: one });
                    }}
                  >
                    {one}
                  </Button>
                ))}
          </Stack>
        }
      >
        <Stack gap={5}>
          <Eyebrow>
            <Mono>{entry.activity_id}</Mono> {entry.version} {lifecycleOf(entry)}{" "}
            <AvailabilityBadge availability={entry.availability} />
          </Eyebrow>

          <div className="cx-brings">
            <span className="cx-key">Canvases</span>
            {canvases.length > 0 ? (
              <div className="cx-canvaslist">
                {canvases.map((canvas) => (
                  <div className="cx-canvasline" key={canvas.kind}>
                    <CanvasKindChip kind={canvas.kind} />
                    <CanvasScopeChip scope={canvas.scope ?? "singleton"} attach={canvas.attach} />
                    <Muted small>
                      {(canvas.roles ?? []).length > 0
                        ? `roles: ${(canvas.roles ?? []).join(", ")}`
                        : "every role"}
                    </Muted>
                    {canvas.required === true ? <span className="cx-req">required</span> : null}
                  </div>
                ))}
              </div>
            ) : (
              <Muted small>None</Muted>
            )}
          </div>

          {roles.length > 0 ? (
            <div className="cx-brings">
              <span className="cx-key">Roles</span>
              <div className="cx-chips">
                {roles.map((role) => (
                  <Chip key={role}>{role}</Chip>
                ))}
              </div>
            </div>
          ) : null}
        </Stack>
      </Panel>

      {added === null ? (
        <RequirementsPreview entry={entry} framed />
      ) : (
        <Requirements added={added} entry={entry} spaceId={spaceId} framed />
      )}
      <AccessOutsideItself access={entry.access} />
      <Settings entry={entry} added={added} />
      {added === null ? null : <GatesEditor addedActivityId={added.added_activity_id} title="Gates" />}
    </>
  );
}

function AccessLine({ address, index }: { readonly address: AccessAddress; readonly index: number }) {
  const named = address.activity_id ?? address.requirement ?? "";
  return (
    <div className="cx-canvasline">
      <Mono>{named === "" ? `position ${String(index + 1)}` : named}</Mono>
      {(address.entity_types ?? []).length > 0 ? (
        <div className="cx-chips">
          {(address.entity_types ?? []).map((type) => (
            <Chip key={type}>{type}</Chip>
          ))}
        </div>
      ) : null}
      <Muted small>{address.via ?? ""}</Muted>
    </div>
  );
}

/** CAT-026: what this version reads and what it applies effects to, derived from its manifest.
 * The screen a customer decides whether they are comfortable on, so it is never behind a disclosure. */
function AccessOutsideItself({ access }: { readonly access: VersionAccess | undefined }) {
  const reads = access?.reads ?? [];
  const writes = access?.writes ?? [];

  return (
    <Panel title="Reads and writes outside itself">
      {reads.length === 0 && writes.length === 0 ? (
        <Muted small>Reaches nothing outside itself</Muted>
      ) : (
        <Stack gap={4}>
          <div className="cx-brings">
            <span className="cx-key">Reads</span>
            {reads.length > 0 ? (
              <div className="cx-canvaslist">
                {reads.map((address, index) => (
                  <AccessLine key={`${address.via ?? ""}-${String(index)}`} address={address} index={index} />
                ))}
              </div>
            ) : (
              <Muted small>Nothing</Muted>
            )}
          </div>
          <div className="cx-brings">
            <span className="cx-key">Writes</span>
            {writes.length > 0 ? (
              <div className="cx-canvaslist">
                {writes.map((address, index) => (
                  <AccessLine key={`${address.via ?? ""}-${String(index)}`} address={address} index={index} />
                ))}
              </div>
            ) : (
              <Muted small>Nothing</Muted>
            )}
          </div>
        </Stack>
      )}
    </Panel>
  );
}

/** One activity, configured. A bundle gets its resolution, its wires and each member in full. */
export function ActivityConfig({
  entry,
  root,
  space,
}: {
  readonly entry: CatalogEntry;
  readonly root: AddedActivityRow | null;
  readonly space: SpaceRow | null;
}) {
  const spaceId = space?.space.space_id ?? "";
  const bundled = entry.manifest.bundle != null;
  const [picked, setPicked] = useState(entry.activity_id);

  const resolution = useBundleResolution(entry.activity_id, bundled);
  const activities = useSpaceActivities(spaceId);
  const pickedQuery = useCatalogEntry(picked);
  const remove = useRemoveActivity();
  const verb = useActivityVerb();

  const members: ResolvedMember[] = resolution.data?.members ?? [];
  const memberIds = members.map((one) => one.activity_id);
  const scope = activities.rows.filter(
    (one) => one.activity_id === entry.activity_id || memberIds.includes(one.activity_id),
  );

  const selected = picked === entry.activity_id ? entry : pickedQuery.entry;
  const selectedRow = scope.find((one) => one.activity_id === picked) ?? null;
  const anyLive = scope.some((one) => isLive(runStatusOf(one.mount)));
  const proposed: ProposedBinding[] = entry.manifest.bundle?.proposed_bindings ?? [];

  const actions =
    root === null ? undefined : bundled ? (
      <Button
        size="sm"
        variant={anyLive ? "destructive" : "primary"}
        loading={verb.isPending}
        onClick={() => {
          verb.mutate({ row: root, verb: anyLive ? "Stop" : "Start" });
        }}
      >
        {anyLive ? "Stop the bundle" : "Start the bundle"}
      </Button>
    ) : undefined;

  return (
    <Page
      eyebrow={space === null ? "Account" : (space.space.name ?? space.space.join_code)}
      title={entry.name}
      actions={actions}
      measure="wide"
    >
      {bundled ? (
        <Panel title="Members" padding="none">
          <DataTable
            caption="Activities this bundle adds"
            loading={resolution.isPending}
            columns={[
              { key: "activity_id", header: "Activity", emphasis: true, mono: true },
              { key: "version", header: "Version", mono: true },
              {
                key: "path",
                header: "Reached through",
                muted: true,
                render: (row: ResolvedMember) => row.path.join(" / "),
              },
              ...(space === null
                ? []
                : [
                    {
                      key: "state",
                      header: "In this space",
                      render: (row: ResolvedMember) => {
                        const held = scope.find((one) => one.activity_id === row.activity_id);
                        return held === undefined ? (
                          <StatusBadge tone="neutral">not added</StatusBadge>
                        ) : (
                          <RunBadge mount={held.mount} />
                        );
                      },
                    },
                  ]),
              {
                key: "open",
                header: "",
                align: "right",
                width: "120px",
                render: (row: ResolvedMember) => (
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Configure ${row.activity_id}`}
                    onClick={() => {
                      setPicked(row.activity_id);
                    }}
                  >
                    Configure
                  </Button>
                ),
              },
            ]}
            rows={members}
            emptyHeadline="No members"
          />
        </Panel>
      ) : null}

      {selected === undefined ? (
        <Panel title="Activity">
          <EmptyState bare headline="Not published" />
        </Panel>
      ) : (
        <NodeConfig entry={selected} added={selectedRow ?? root} spaceId={spaceId} />
      )}

      {bundled ? (
        <Panel title="Proposed wires" padding="none">
          <DataTable
            caption="Wires this bundle proposes between its members"
            columns={[
              { key: "source", header: "From", emphasis: true, mono: true },
              { key: "export", header: "Sends", mono: true },
              { key: "target", header: "To", mono: true },
              { key: "trigger", header: "Takes", mono: true },
              {
                key: "enabled_by_default",
                header: "By default",
                render: (row: ProposedBinding) =>
                  row.enabled_by_default === false ? (
                    <StatusBadge tone="neutral">off</StatusBadge>
                  ) : (
                    <StatusBadge tone="success">on</StatusBadge>
                  ),
              },
            ]}
            rows={proposed}
            emptyHeadline="None proposed"
          />
        </Panel>
      ) : null}

      {bundled && root !== null ? (
        <Panel
          title="Removing this bundle"
          tone="sunken"
          action={
            <Button
              size="sm"
              variant="destructive"
              loading={remove.isPending}
              onClick={() => {
                remove.mutate(root.added_activity_id);
              }}
            >
              Remove the bundle
            </Button>
          }
        >
          <div className="cx-chips">
            {members.map((member) => (
              <Chip key={member.activity_id} tone={member.removed_with_root ? undefined : "ns"}>
                {`${member.activity_id}${member.removed_with_root ? "" : ", stays"}`}
              </Chip>
            ))}
          </div>
        </Panel>
      ) : null}
    </Page>
  );
}
